import "./DefaultDropdown.scss";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import SearchInput from "../../inputs/SearchInput/SearchInput";
import arrowIcon from "assets/icons/arrow-down.svg";
import classNames from "classnames";
import ImageWithFallback from "helpers/ImageWithFallback";
import Loader from "components/global/Loader";
import Tooltip from "../../Tooltip";
import useFixedDropdownPosition from "../useFixedDropdownPosition";

interface Props {
  nativeSelect?: boolean;
  ariaLabel?: string;
  value?: string | number;
  selectedLabel?: string;
  placeholder?: string | number;
  label?: string;
  disabled?: boolean;
  hasError?: boolean;
  loading?: boolean;
  isRequired?: boolean;
  options: {
    value: string | number;
    value2?: string | number;
    label: string;
    img?: string;
    icon?: React.ReactNode;
  }[];
  onSelect: (e: string | number, e2?: string | number) => void;
  isSmall?: boolean;
  isShort?: boolean;
  isBot?: boolean;
  isTop?: boolean;
  search?: string;
  onSearch?: (e: string) => void;
  onLoadMore?: () => void;
  hasMore?: boolean;
  emptyText?: string;
  tooltipText?: string;
  tooltipPosition?:
    | "top"
    | "right"
    | "bottom"
    | "left"
    | "top-right"
    | "top-left"
    | "bottom-left"
    | "bottom-right";
  isEllipsis?: boolean;
  useFixedMenu?: boolean;
  menuMaxHeight?: number;
  className?: string;
}

const DefaultDropdown: React.FC<Props> = ({
  nativeSelect = false,
  ariaLabel,
  value,
  selectedLabel,
  label,
  placeholder,
  options,
  onSelect,
  disabled,
  isSmall,
  isShort,
  isBot,
  isTop,
  search,
  onSearch,
  onLoadMore,
  hasMore = false,
  emptyText,
  hasError,
  loading,
  isRequired,
  tooltipText,
  tooltipPosition,
  isEllipsis,
  useFixedMenu = false,
  menuMaxHeight,
  className,
}) => {
  const [dropdownOpened, setDropdownOpened] = useState(false);
  const [currentItem, setCurrentItem] = useState<{
    value: string | number;
    label: string;
    img?: string;
    icon?: React.ReactNode;
  }>();
  const [openUp, setOpenUp] = useState(false);
  const [isFixedMenuMounted, setIsFixedMenuMounted] = useState(false);
  const loadMoreTriggeredRef = useRef(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const fixedMenuStyle = useFixedDropdownPosition(
    triggerRef,
    dropdownOpened,
    useFixedMenu,
    menuMaxHeight ?? 230,
    Boolean(isTop && !isBot),
    Boolean(isBot),
  );

  useEffect(() => {
    if (!useFixedMenu) return;

    if (dropdownOpened) {
      setIsFixedMenuMounted(true);
      return;
    }

    const timeout = window.setTimeout(() => {
      setIsFixedMenuMounted(false);
    }, 250);

    return () => window.clearTimeout(timeout);
  }, [dropdownOpened, useFixedMenu]);

  useEffect(() => {
    setCurrentItem(
      options.find((el) => el?.value?.toString() === value?.toString()),
    );
  }, [options, value]);

  const handleChange = (e: string | number, e2?: string | number) => {
    onSelect(e, e2);
  };

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const fitsDown = window.innerHeight - rect.bottom >= 150;
    setOpenUp(isBot ? false : !fitsDown || !!isTop);
  }, [isBot, isTop, dropdownOpened]);

  useEffect(() => {
    const onDocDown = (e: MouseEvent) => {
      const el = containerRef.current;
      if (!el) return;
      if (
        !el.contains(e.target as Node) &&
        !menuRef.current?.contains(e.target as Node)
      ) {
        setDropdownOpened(false);
      }
    };
    document.addEventListener("mousedown", onDocDown);
    return () => document.removeEventListener("mousedown", onDocDown);
  }, []);

  useEffect(() => {
    const onCloseAll = () => setDropdownOpened(false);
    window.addEventListener("app:closeDropdowns", onCloseAll);
    return () => window.removeEventListener("app:closeDropdowns", onCloseAll);
  }, []);

  const handleToggle = useCallback(() => {
    if (disabled) return;
    if (!dropdownOpened) {
      window.dispatchEvent(new Event("app:closeDropdowns"));
    }
    setDropdownOpened((v) => !v);
  }, [dropdownOpened, disabled]);

  const handleOptionsScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.currentTarget;
    const isNearBottom =
      target.scrollHeight - target.scrollTop - target.clientHeight < 40;

    if (!isNearBottom) {
      loadMoreTriggeredRef.current = false;
      return;
    }

    if (hasMore && !loading && !loadMoreTriggeredRef.current) {
      loadMoreTriggeredRef.current = true;
      onLoadMore?.();
    }
  };

  if (nativeSelect) {
    return (
      <div
        className={classNames("default-dropdown", className, {
          "default-dropdown--error": hasError,
        })}
      >
        <select
          className="default-dropdown__select default-dropdown__select--native"
          aria-label={ariaLabel ?? label}
          aria-invalid={hasError || undefined}
          disabled={disabled}
          value={
            options.some((option) => String(option.value) === String(value))
              ? value
              : ""
          }
          onChange={(event) => onSelect(event.target.value)}
        >
          <option value="" disabled>
            {placeholder}
          </option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
    );
  }

  const dropdownContent = (
    <div
      ref={menuRef}
      className={classNames("default-dropdown__content", {
        "default-dropdown__content--opened": dropdownOpened,
        "default-dropdown__content--portal": useFixedMenu,
      })}
      style={{
        ...fixedMenuStyle,
        ...(!useFixedMenu && menuMaxHeight ? { maxHeight: menuMaxHeight } : {}),
      }}
    >
      {onSearch && (
        <SearchInput
          placeholder="Search"
          value={search ?? ""}
          onChange={onSearch}
        />
      )}
      <div className="default-dropdown__options" onScroll={handleOptionsScroll}>
        {!loading && options.length === 0 ? (
          <div className="default-dropdown__empty">
            {search ? "Nothing found" : (emptyText ?? "No options available")}
          </div>
        ) : loading && options.length === 0 ? (
          <div className="default-dropdown__empty">
            <Loader size={10} />
          </div>
        ) : (
          <>
            {options.map((el, idx) => (
              <div
                key={`${el.value}-${idx}`}
                className={`
                default-dropdown__item
                ${
                  String(value) === String(el.value)
                    ? "default-dropdown__item--active"
                    : ""
                }
              `}
                onClick={(event) => {
                  event.stopPropagation();
                  if (!disabled) {
                    handleChange(el.value, el.value2);
                    setDropdownOpened(false);
                  }
                }}
              >
                <div className="default-dropdown__item-content">
                  {el.icon}
                  {el.img && (
                    <ImageWithFallback
                      key={currentItem?.value}
                      src={el.img}
                      alt={el.label}
                      className="default-dropdown__item-img"
                    />
                  )}

                  <span
                    title={el.label}
                    className={classNames({
                      "default-dropdown__text--ellipsis": isEllipsis,
                    })}
                  >
                    {el.label}
                  </span>
                </div>
              </div>
            ))}
            {loading && (
              <div className="default-dropdown__loader">
                <Loader size={10} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );

  return (
    <div
      ref={containerRef}
      className={classNames("default-dropdown", className, {
        "default-dropdown--short": isShort,
        "default-dropdown--small": isSmall,
        "default-dropdown--with-label": label,
        "default-dropdown--opened": dropdownOpened,
        "default-dropdown--open-up": openUp,
        "default-dropdown--error": hasError,
      })}
    >
      {label && (
        <div className="default-dropdown__label">
          <span>{label}</span>
          {isRequired && <span className="default-dropdown__required">*</span>}

          {tooltipText && (
            <Tooltip text={tooltipText} position={tooltipPosition}>
              <button
                className="info-icon"
                onClick={(e) => e.stopPropagation()}
              >
                ℹ
              </button>
            </Tooltip>
          )}
        </div>
      )}

      <div
        ref={triggerRef}
        className={`
        default-dropdown__select
        ${disabled ? "default-dropdown__select--disabled" : ""}
        ${dropdownOpened ? "default-dropdown__select--opened" : ""}
      `}
        onClick={(e) => {
          e.stopPropagation();
          handleToggle();
        }}
      >
        {currentItem?.icon}
        {currentItem?.img && (
          <ImageWithFallback
            key={currentItem?.value}
            src={currentItem.img}
            alt={currentItem.label}
            className="default-dropdown__item-img"
          />
        )}
        <span
          title={String(currentItem?.label ?? placeholder ?? "")}
          className={classNames({
            "default-dropdown__text--ellipsis": isEllipsis,
          })}
        >
          {currentItem?.label ?? selectedLabel ?? placeholder ?? ""}
        </span>
        <img src={arrowIcon} alt="arrow" className="default-dropdown__arrow" />
      </div>

      {useFixedMenu
        ? (dropdownOpened || isFixedMenuMounted) &&
          createPortal(dropdownContent, document.body)
        : dropdownContent}
    </div>
  );
};

export default DefaultDropdown;
